import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams, Redirect, useHistory } from "react-router-dom";
import RefereeDashboardLayout from "./sections/referee/RefereeDashboardLayout";
import {
  loadCourseReferees,
  loadProfile,
  getCachedProfile,
  setCachedProfile,
} from "../utils/Network";
import { getDefaultName, getDefaultValue, logout } from "../utils";
import { useStateValue } from "../StateProvider";
import RefereePanel from "./RefereePanel";

function formatDateRange(course) {
  if (!course?.startdate && !course?.enddate) return "";
  const s = course.startdate ? new Date(course.startdate).toLocaleDateString() : "—";
  const e = course.enddate ? new Date(course.enddate).toLocaleDateString() : "—";
  return `${s} – ${e}`;
}

export default function RefereeContest() {
  const history = useHistory();
  const { courseId } = useParams();
  const idNum = parseInt(courseId, 10);
  const [profile, setProfile] = useState(() => getCachedProfile());
  const [assignments, setAssignments] = useState(null);
  const [loading, setLoading] = useState(true);

  const [{ translations }] = useStateValue();
  const translate = (str) => {
    if (!translations) return str;
    const t = translations.find((x) => x.key === str);
    if (t) return getDefaultValue(t);
    return str;
  };

  useEffect(() => {
    if (!Number.isFinite(idNum)) {
      setLoading(false);
      return;
    }
    loadProfile().then((p) => {
      if (!p) {
        logout();
        window.location.href = "/signin";
        return;
      }
      if (p.role !== "referee") {
        window.location.href = "/signout";
        return;
      }
      setProfile(p);
      setCachedProfile(p);
      loadCourseReferees().then((rows) => {
        setAssignments(Array.isArray(rows) ? rows : []);
        setLoading(false);
      });
    });
  }, [idNum]);

  const assignment = useMemo(() => {
    if (!assignments || !Number.isFinite(idNum)) return null;
    return assignments.find((r) => r.course && r.course.id === idNum) || null;
  }, [assignments, idNum]);

  if (!Number.isFinite(idNum)) {
    return <Redirect to="/referee/dashboard" />;
  }

  if (!loading && assignments && !assignment) {
    return <Redirect to="/referee/dashboard" />;
  }

  const course = assignment?.course;

  return (
    <RefereeDashboardLayout
      profile={profile}
      onProfileUpdated={(p) => {
        setProfile(p);
        setCachedProfile(p);
      }}
      assignments={assignments || []}
      selectedCourseId={idNum}
      onSelectCourse={(id) => {
        history.push(`/referee/contest/${id}`);
      }}
      translate={translate}
    >
      <Link to="/referee/dashboard" className="referee-jury-back mb-3 d-inline-flex">
        <i className="fas fa-arrow-left" aria-hidden="true" />
        {translate("Dashboard")}
      </Link>

      {loading && (
        <div className="text-center py-5">
          <div className="bounce-loader d-inline-block">
            <div className="bounce1" />
            <div className="bounce2" />
            <div className="bounce3" />
          </div>
        </div>
      )}
      {!loading && assignment && (
        <RefereePanel
          course={idNum}
          title={course ? getDefaultName(course.names) : translate("Contest")}
          dates={course ? formatDateRange(course) : ""}
        />
      )}
    </RefereeDashboardLayout>
  );
}
